import { useMemo } from "react";
import { useSelector } from "react-redux";

import { useAuth0V2 } from "@stadium/services/auth0";

import useLogout from "@hooks/use-logout";
import { useCurrentUserContext } from "@stadium/hooks/lib/use-current-user";
import { useWorkspaceContext } from "@contexts/workspace-context";

import StadiumHeader from "@stadium/shared/header";
import { StadiumSharedConfigWrapper } from "@stadium/shared/global-config";

import { getStadiumSharedConfig } from "@helpers/config";
import { getStoreAccessibilityEnabled } from "@selectors/store";

import { LOGIN_CALLBACK_ROUTE } from "@constants/routes";
import { BYSTADIUM_HOSTNAME, DASHBOARD_URL } from "@stadium/helpers/src/app-routes";
import { SHOPS } from "@stadium/constants";

const emptyObj = {};

const DefaultStadiumHeader = ({
  isWeglotDropDown = true,
  className = "",
  loginOptions = emptyObj,
  showUserFunds = true,
  isMarketingPage = false,
}) => {
  const { currentUserData } = useCurrentUserContext();
  const { name, firstName, lastName, email, spreeId: id, avatarUrl } = currentUserData;
  const { permissions, isPersonalWorkspace } = useWorkspaceContext();
  const { user, isAuthenticated, isLoading } = useAuth0V2();

  const { accessibilityEnabled } = useSelector(getStoreAccessibilityEnabled);

  const profileData = useMemo(
    () => ({
      authUsername: user?.name,
      name,
      firstName,
      lastName,
      email,
      avatarUrl,
      id,
      canViewShops: permissions.canViewShops,
    }),
    [email, id, name, user, avatarUrl, permissions.canViewShops],
  );

  const { logoutAction } = useLogout();

  const HeaderView = useMemo(
    () => (
      <StadiumHeader
        profileData={profileData}
        logout={logoutAction}
        isWeglotDropDown={isWeglotDropDown}
        isAuthenticated={isAuthenticated}
        loginOptions={{ loginCallback: LOGIN_CALLBACK_ROUTE, ...loginOptions }}
        isLoading={isLoading}
        className={className}
        headerLogoLink={isAuthenticated ? DASHBOARD_URL : BYSTADIUM_HOSTNAME}
        showUserFunds={showUserFunds}
        accessibilityEnabled={accessibilityEnabled}
        application={SHOPS}
        isMarketingPage={isMarketingPage}
        personalWorkspaceNudge={isPersonalWorkspace}
      />
    ),
    [profileData, logoutAction, isLoading, isAuthenticated, isMarketingPage, isPersonalWorkspace],
  );

  return (
    <StadiumSharedConfigWrapper value={getStadiumSharedConfig}>
      {HeaderView}
    </StadiumSharedConfigWrapper>
  );
};

export default DefaultStadiumHeader;
